import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, CheckCircle2, ChevronRight, Clock, Info, ShoppingBag, Sparkles, User } from "lucide-react";

import { useSupabaseSession } from "@/hooks/use-supabase-session";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type PackageKey = "pin" | "isim" | "normal" | "premium" | "iliski" | "test";

type OrderStatus = "pending" | "paid" | "cancelled";

type OrderResponse = {
  ok: boolean;
  order: {
    id: string;
    status: OrderStatus;
    totalTry: number;
    createdAt: number;
    paidAt: number | null;
    packageCount: number;
    productCount: number;
  };
};

const GOLD = "#d4af37";

function normalizePackage(input: string | null): PackageKey {
  const v = (input ?? "").toLowerCase().trim();
  if (v === "pin" || v === "isim" || v === "normal" || v === "premium" || v === "iliski" || v === "test") {
    return v;
  }
  return "normal";
}

function parseLang(value: string | null) {
  const v = (value ?? "tr").toLowerCase().trim();
  return v === "en" || v === "es" || v === "de" ? v : "tr";
}

async function fetchOrder(id: string): Promise<OrderResponse> {
  const res = await fetch(`/api/numeroloji/orders/${encodeURIComponent(id)}`);
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Sipariş bulunamadı.");
  }
  return json as OrderResponse;
}

type SubmitResponse =
  | { ok: true; requestId: string; nextPackageKey?: string | null }
  | { ok: false; error: string };

type NextPackageResponse =
  | { ok: true; nextPackageKey: string | null; remainingCount: number; totalCount: number }
  | { ok: false; error: string };

async function fetchNextPackage(input: { orderId: string; token: string }): Promise<NextPackageResponse> {
  const url = new URL(`/api/numeroloji/orders/${encodeURIComponent(input.orderId)}/next-package`, window.location.origin);
  url.searchParams.set("token", input.token);

  const res = await fetch(url.pathname + url.search);
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok || !json) {
    throw new Error(typeof json?.error === "string" ? json.error : "Paket bilgisi alınamadı.");
  }
  return json as NextPackageResponse;
}

async function submitRequest(payload: Record<string, unknown>): Promise<SubmitResponse> {
  const res = await fetch("/api/numeroloji/requests", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    return {
      ok: false as const,
      error: typeof json?.error === "string" ? json.error : "Gönderim sırasında hata oluştu.",
    };
  }

  if (json && typeof json.requestId === "string" && json.requestId) {
    const nextPackageKey =
      typeof json.nextPackageKey === "string" ? json.nextPackageKey : json.nextPackageKey === null ? null : undefined;
    return { ok: true as const, requestId: json.requestId, nextPackageKey };
  }

  return { ok: false as const, error: "İstek oluşturulamadı." };
}

function splitFullName(fullName: string) {
  const parts = String(fullName ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const ad = parts[0] ?? "";
  const soyad = parts.slice(1).join(" ");
  return { ad, soyad };
}

export default function NumerolojiForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const packageParam = useMemo(() => (searchParams.get("package") ?? "").trim(), [searchParams]);
  const orderId = useMemo(() => (searchParams.get("orderId") ?? "").trim(), [searchParams]);
  const token = useMemo(() => (searchParams.get("token") ?? "").trim(), [searchParams]);
  const reportLang = useMemo(() => parseLang(searchParams.get("lang")), [searchParams]);
  const seqParam = useMemo(() => (searchParams.get("seq") ?? "").trim(), [searchParams]);

  const [resolvedPackageKey, setResolvedPackageKey] = useState<PackageKey | null>(null);
  const [flowInfo, setFlowInfo] = useState<{ remainingCount: number; totalCount: number } | null>(null);

  useEffect(() => {
    if (!orderId || !token) return;

    let cancelled = false;
    fetchNextPackage({ orderId, token })
      .then((resp) => {
        if (cancelled) return;
        if (resp.ok === false) return;

        setFlowInfo({ remainingCount: resp.remainingCount, totalCount: resp.totalCount });

        if ((Number(resp.totalCount) || 0) <= 0 || !resp.nextPackageKey) {
          setResolvedPackageKey(null);
          return;
        }

        setResolvedPackageKey(normalizePackage(resp.nextPackageKey));
      })
      .catch(() => {
        // ignore
      });

    return () => {
      cancelled = true;
    };
  }, [orderId, token]);

  const effectivePackageKey = resolvedPackageKey;

  const flowStep = useMemo(() => {
    if (!flowInfo) return null;
    const total = Math.max(0, Math.floor(Number(flowInfo.totalCount) || 0));
    const remaining = Math.max(0, Math.floor(Number(flowInfo.remainingCount) || 0));
    if (!total) return null;
    const step = Math.min(total, Math.max(1, total - remaining + 1));
    return { step, total, remaining, remainingAfterThis: Math.max(0, remaining - 1) };
  }, [flowInfo]);

  const isRelationship = effectivePackageKey === "iliski";
  const isPinAnalysis = effectivePackageKey === "pin";
  const isNameOnlyAnalysis = effectivePackageKey === "isim";
  const shouldAskFullName = !isPinAnalysis;
  const shouldAskEmail = !isPinAnalysis && !isNameOnlyAnalysis;
  const shouldAskBirthDate = effectivePackageKey !== "isim";
  const shouldAskBirthTime = !isPinAnalysis && !isNameOnlyAnalysis;

  const { data: orderData, isLoading: orderLoading, error: orderError } = useQuery({
    queryKey: ["numeroloji-order", orderId],
    queryFn: () => fetchOrder(orderId),
    enabled: Boolean(orderId),
    staleTime: 15_000,
  });

  const orderStatus = orderData?.order?.status;
  const isFlowLoading = Boolean(orderId && token) && flowInfo === null;
  const rawAnalysisPackageCount = Number(flowInfo?.totalCount ?? 0);
  const shouldSkipAnalysisForProductOnlyOrder =
    !packageParam && Number(orderData?.order?.productCount ?? 0) > 0 && rawAnalysisPackageCount <= 1;
  const hasAnalysisPackage = rawAnalysisPackageCount > 0 && !shouldSkipAnalysisForProductOnlyOrder;
  const showNoAnalysisSuccess = orderStatus === "paid" && flowInfo !== null && !hasAnalysisPackage;
  const canSubmit = Boolean(orderId && token && resolvedPackageKey) && orderStatus === "paid" && hasAnalysisPackage;

  const { user } = useSupabaseSession();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    // When moving to a different package within the same order (or to the next form instance), clear form fields.
    // (Email stays, since delivery is per-account.)
    setFullName("");
    setBirthDate("");
    setBirthTime("");
    setPartnerFullName("");
    setPartnerBirthDate("");
    setStatus({ state: "idle" });
  }, [effectivePackageKey, seqParam]);

  const accountEmail = String(user?.email ?? "").trim();
  const emailLocked = Boolean(accountEmail);

  useEffect(() => {
    if (!user) return;

    if (accountEmail && email !== accountEmail) setEmail(accountEmail);

    const meta = ((user.metadata ?? {}) as Record<string, unknown>) || {};
    const fullNameRaw =
      (typeof meta.full_name === "string" ? meta.full_name : "") ||
      (typeof meta.name === "string" ? meta.name : "") ||
      (typeof meta.display_name === "string" ? meta.display_name : "");

    if (!fullName && fullNameRaw) setFullName(String(fullNameRaw));
  }, [accountEmail, email, fullName, user]);
  const [birthDate, setBirthDate] = useState("");
  const [birthTime, setBirthTime] = useState("");

  const [partnerFullName, setPartnerFullName] = useState("");
  const [partnerBirthDate, setPartnerBirthDate] = useState("");


  const [status, setStatus] = useState<
    | { state: "idle" }
    | { state: "submitting" }
    | { state: "error"; message: string }
  >({ state: "idle" });

  if (!orderId || !token) {
    return (
      <div className="mx-auto grid w-full max-w-2xl gap-4 p-6">
        <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-200">
          Erişim bağlantısı eksik. Bu sayfa ödeme akışından sonra verilen bağlantıyla açılmalıdır.
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button asChild className="rounded-full">
            <Link to="/numeroloji/magaza">Mağazaya git</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-full">
            <Link to="/numeroloji">Ana sayfa</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-[#050508] text-slate-200">
      <div
        className="pointer-events-none fixed inset-0 -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(circle at 50% 0%, rgba(212,175,55,0.16) 0%, rgba(10,10,12,0) 55%), radial-gradient(circle at 20% 25%, rgba(127,13,242,0.12) 0%, rgba(10,10,12,0) 55%), radial-gradient(circle at 80% 70%, rgba(212,175,55,0.10) 0%, rgba(10,10,12,0) 60%)",
        }}
      />

      <nav className="sticky top-0 z-50 border-b border-white/10 bg-[#050508]/80 backdrop-blur-md">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4">
          <Link to="/numeroloji" className="flex items-center gap-3">
            <span className="grid h-8 w-8 place-items-center border border-[#d4af37] rotate-45">
              <span className="h-3 w-3 bg-[#d4af37]" />
            </span>
            <span className="font-display text-lg font-bold tracking-[0.16em] text-white">Numeroloji.app</span>
          </Link>

          <div className="flex items-center gap-3 sm:gap-6">
            <Link
              to="/panel"
              className="rounded-full border border-[rgba(212,175,55,0.35)] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-100 transition-colors hover:bg-[rgba(212,175,55,0.14)] hover:text-white sm:text-xs"
            >
              Panele Git
            </Link>
            <Link
              to="/numeroloji/magaza"
              className="font-display text-xs tracking-[0.22em] transition-colors hover:text-[rgba(212,175,55,0.95)] sm:text-sm"
            >
              MAĞAZA
            </Link>
            <Link
              to="/numeroloji/sepet"
              className="font-display text-xs tracking-[0.22em] transition-colors hover:text-[rgba(212,175,55,0.95)] sm:text-sm"
            >
              SEPET
            </Link>
            <Link to="/numeroloji/sepet" className="relative">
              <ShoppingBag className="h-5 w-5 text-slate-200" />
            </Link>
          </div>
        </div>
      </nav>

      <main className="mx-auto w-full max-w-4xl px-4 py-10 sm:px-6 sm:py-14">
        <div className="mb-10">
          <div className="flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
            <Link to="/numeroloji" className="transition-colors hover:text-[rgba(212,175,55,0.95)]">
              Ana Sayfa
            </Link>
            <ChevronRight className="h-4 w-4 opacity-60" />
            <Link to="/numeroloji/sepet" className="transition-colors hover:text-[rgba(212,175,55,0.95)]">
              Sepetim
            </Link>
            <ChevronRight className="h-4 w-4 opacity-60" />
            <span style={{ color: GOLD }}>Bilgiler</span>
          </div>
        </div>

        {/* Success Header */}
        <div className="mb-12 flex flex-col items-center text-center">
          <div
            className="grid h-20 w-20 place-items-center rounded-full border"
            style={{ backgroundColor: "rgba(212,175,55,0.14)", borderColor: "rgba(212,175,55,0.28)" }}
          >
            <CheckCircle2 className="h-10 w-10" style={{ color: GOLD }} />
          </div>

          <h1 className="mt-6 font-display text-3xl font-semibold tracking-tight text-white sm:text-5xl">
            Ödemeniz Başarıyla Tamamlandı!
          </h1>

          {flowStep && flowStep.total > 1 ? (
            <div className="mt-4 rounded-2xl border px-4 py-3 text-sm" style={{ borderColor: "rgba(212,175,55,0.18)", backgroundColor: "rgba(0,0,0,0.20)" }}>
              <div className="font-semibold text-slate-100">
                Form {flowStep.step}/{flowStep.total}
              </div>
              <div className="mt-1 text-slate-400">
                Bu formu gönderince {flowStep.remainingAfterThis} form daha dolduracaksınız.
              </div>
            </div>
          ) : null}
        </div>

        {/* Main Form */}
        <section
          className="relative overflow-hidden rounded-3xl border p-6 shadow-2xl sm:p-10"
          style={{
            borderColor: "rgba(212,175,55,0.18)",
            background:
              "radial-gradient(circle at 70% 10%, rgba(212,175,55,0.12) 0%, transparent 45%), radial-gradient(circle at 15% 30%, rgba(127,13,242,0.10) 0%, transparent 55%), linear-gradient(145deg, rgba(26,18,37,0.55), rgba(10,10,12,0.72))",
          }}
        >
          <div className="pointer-events-none absolute right-6 top-4 opacity-[0.08]" aria-hidden>
            <Sparkles className="h-20 w-20" style={{ color: GOLD }} />
          </div>

          <h2 className="flex items-center gap-3 font-display text-xl font-semibold tracking-tight text-white sm:text-2xl">
            <span
              className="grid h-10 w-10 place-items-center rounded-xl border"
              style={{ borderColor: "rgba(212,175,55,0.20)", backgroundColor: "rgba(212,175,55,0.08)" }}
            >
              <Sparkles className="h-5 w-5" style={{ color: GOLD }} />
            </span>
            <span style={{ color: GOLD }}>
              {showNoAnalysisSuccess ? "Siparişiniz başarıyla alındı" : "Analizinizin hazırlanması için : Son Bir Adım!"}
            </span>
          </h2>

          {orderLoading || (orderStatus === "paid" && isFlowLoading) ? (
            <div className="mt-6 rounded-2xl border p-4 text-sm" style={{ borderColor: "rgba(212,175,55,0.12)", backgroundColor: "rgba(0,0,0,0.20)" }}>
              Sipariş kontrol ediliyor...
            </div>
          ) : orderError ? (
            <div className="mt-6 rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-200">
              {(orderError as Error).message}
            </div>
          ) : orderStatus !== "paid" ? (
            <div className="mt-6 grid gap-3 rounded-2xl border p-5" style={{ borderColor: "rgba(212,175,55,0.12)", backgroundColor: "rgba(0,0,0,0.20)" }}>
              <div className="text-sm font-semibold text-white">Ödeme onayı bekleniyor</div>
              <div className="text-sm text-slate-400">Ödeme onaylandıktan sonra bu form aktif olur.</div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button asChild className="rounded-full">
                  <Link
                    to={`/numeroloji/odeme?orderId=${encodeURIComponent(orderId)}&token=${encodeURIComponent(token)}${packageParam ? `&package=${encodeURIComponent(packageParam)}` : ""}&lang=${encodeURIComponent(reportLang)}`}
                  >
                    Ödeme sayfasına dön
                  </Link>
                </Button>
                <Button asChild variant="outline" className="rounded-full">
                  <Link to="/numeroloji">Ana sayfa</Link>
                </Button>
              </div>
            </div>
          ) : !hasAnalysisPackage ? (
            <div className="mt-6 grid gap-4 rounded-2xl border p-5" style={{ borderColor: "rgba(212,175,55,0.12)", backgroundColor: "rgba(0,0,0,0.20)" }}>
              <div className="text-sm font-semibold text-white">Ödemeniz başarıyla tamamlandı.</div>
              <div className="text-sm text-slate-400">Bu siparişte numeroloji analizi bulunmuyor. Sipariş detayınızı panelinizden takip edebilirsiniz.</div>
              <div>
                <Button asChild className="rounded-full">
                  <Link to="/panel/siparislerim">Panelime git</Link>
                </Button>
              </div>
            </div>
          ) : (
            <form
              className="mt-8 grid grid-cols-1 gap-6"
              onSubmit={async (e) => {
                e.preventDefault();
                if (!canSubmit || status.state === "submitting") return;

                setStatus({ state: "submitting" });

                if (!effectivePackageKey) {
                  setStatus({ state: "error", message: "Bu sipariş için analiz formu bulunmuyor." });
                  return;
                }

                let ad = "";
                let soyad = "";

                if (shouldAskFullName) {
                  const parsed = splitFullName(fullName);
                  if (!parsed.ad || !parsed.soyad) {
                    setStatus({ state: "error", message: "Lütfen tam ad ve soyad girin." });
                    return;
                  }
                  ad = parsed.ad;
                  soyad = parsed.soyad;
                } else {
                  ad = "Pin";
                  soyad = "Analizi";
                }

                const emailValue = String(email || accountEmail).trim();
                if (!emailValue || !emailValue.includes("@")) {
                  setStatus({ state: "error", message: "Geçerli hesap e-postası gerekli." });
                  return;
                }

                const payload: Record<string, unknown> = {
                  orderId,
                  token,
                  packageKey: effectivePackageKey,
                  reportLang,
                  ad,
                  soyad,
                  email: emailValue,
                };

                if (shouldAskBirthDate) {
                  // birthDate is YYYY-MM-DD
                  const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(birthDate);
                  if (!m) {
                    setStatus({ state: "error", message: "Lütfen doğum tarihini seçin." });
                    return;
                  }
                  payload.yil = Number(m[1]);
                  payload.ay = Number(m[2]);
                  payload.gun = Number(m[3]);
                }

                if (isRelationship) {
                  const p = splitFullName(partnerFullName);
                  if (!p.ad || !p.soyad) {
                    setStatus({ state: "error", message: "İlişki analizi için kişi B adı/soyadı gerekli." });
                    return;
                  }
                  payload.partnerAd = p.ad;
                  payload.partnerSoyad = p.soyad;

                  const pm = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(partnerBirthDate);
                  if (!pm) {
                    setStatus({ state: "error", message: "İlişki analizi için kişi B doğum tarihi gerekli." });
                    return;
                  }
                  payload.partnerYil = Number(pm[1]);
                  payload.partnerAy = Number(pm[2]);
                  payload.partnerGun = Number(pm[3]);
                }

                const result = await submitRequest(payload);
                if (result.ok === false) {
                  setStatus({ state: "error", message: result.error });
                  return;
                }

                const nextKey = String(result.nextPackageKey ?? "").trim();
                if (nextKey) {
                  const url = new URL("/numeroloji/form", window.location.origin);
                  url.searchParams.set("orderId", orderId);
                  url.searchParams.set("token", token);
                  url.searchParams.set("package", nextKey);
                  url.searchParams.set("lang", reportLang);
                  url.searchParams.set("seq", result.requestId);
                  navigate(url.pathname + url.search, { replace: true });
                  return;
                }

                navigate(`/numeroloji/basarili?requestId=${encodeURIComponent(result.requestId)}`, { replace: true });
              }}
            >
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {shouldAskFullName ? (
                  <div className="md:col-span-2 grid gap-2">
                    <Label className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-300">Tam İsim (Analiz için)</Label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 opacity-60" style={{ color: GOLD }} />
                      <Input
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        required
                        placeholder="Adınız ve Soyadınız"
                        className="h-12 rounded-xl border-[rgba(212,175,55,0.22)] bg-black/25 pl-11 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[rgba(212,175,55,0.55)]"
                      />
                    </div>
                  </div>
                ) : null}

                {shouldAskEmail ? (
                  <div className="md:col-span-2 grid gap-2">
                    <Label className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-300">E-posta</Label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 opacity-60" style={{ color: GOLD }} />
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => {
                          if (emailLocked) return;
                          setEmail(e.target.value);
                        }}
                        required
                        readOnly={emailLocked}
                        placeholder="ornek@mail.com"
                        className="h-12 rounded-xl border-[rgba(212,175,55,0.22)] bg-black/25 pl-11 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[rgba(212,175,55,0.55)] read-only:opacity-80"
                      />
                    </div>
                    <div className="text-xs text-slate-500">
                      Rapor bu e-posta adresine teslim edilir.
                      {emailLocked ? " (Hesabınızdaki e-posta kullanılır.)" : ""}
                    </div>
                  </div>
                ) : null}

                {shouldAskBirthDate ? (
                  <div className="grid gap-2">
                    <Label className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-300">Doğum Tarihi</Label>
                    <div className="relative">
                      <CalendarDays className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 opacity-60" style={{ color: GOLD }} />
                      <Input
                        type="date"
                        value={birthDate}
                        onChange={(e) => setBirthDate(e.target.value)}
                        required
                        className="h-12 rounded-xl border-[rgba(212,175,55,0.22)] bg-black/25 pl-11 text-slate-100 focus-visible:ring-[rgba(212,175,55,0.55)] [color-scheme:dark]"
                      />
                    </div>
                  </div>
                ) : null}

                {shouldAskBirthTime ? (
                  <div className="grid gap-2">
                    <Label className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-300">Doğum Saati (Opsiyonel)</Label>
                    <div className="relative">
                      <Clock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 opacity-60" style={{ color: GOLD }} />
                      <Input
                        type="time"
                        value={birthTime}
                        onChange={(e) => setBirthTime(e.target.value)}
                        className="h-12 rounded-xl border-[rgba(212,175,55,0.22)] bg-black/25 pl-11 text-slate-100 focus-visible:ring-[rgba(212,175,55,0.55)] [color-scheme:dark]"
                      />
                    </div>
                  </div>
                ) : null}


                {isRelationship ? (
                  <div className="md:col-span-2 mt-2 rounded-2xl border border-[rgba(212,175,55,0.12)] bg-black/20 p-5">
                    <div className="text-sm font-semibold text-white">İlişki analizi için kişi B bilgileri</div>

                    <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div className="md:col-span-2 grid gap-2">
                        <Label className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-300">Kişi B Tam İsim</Label>
                        <div className="relative">
                          <User className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 opacity-60" style={{ color: GOLD }} />
                          <Input
                            value={partnerFullName}
                            onChange={(e) => setPartnerFullName(e.target.value)}
                            required
                            placeholder="Adı ve Soyadı"
                            className="h-12 rounded-xl border-[rgba(212,175,55,0.22)] bg-black/25 pl-11 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[rgba(212,175,55,0.55)]"
                          />
                        </div>
                      </div>

                      <div className="grid gap-2">
                        <Label className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-300">Kişi B Doğum Tarihi</Label>
                        <div className="relative">
                          <CalendarDays className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 opacity-60" style={{ color: GOLD }} />
                          <Input
                            type="date"
                            value={partnerBirthDate}
                            onChange={(e) => setPartnerBirthDate(e.target.value)}
                            required
                            className="h-12 rounded-xl border-[rgba(212,175,55,0.22)] bg-black/25 pl-11 text-slate-100 focus-visible:ring-[rgba(212,175,55,0.55)] [color-scheme:dark]"
                          />
                        </div>
                      </div>

                      <div className="grid gap-2">
                        <Label className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-300">Kişi B Doğum Saati (Opsiyonel)</Label>
                        <div className="relative">
                          <Clock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 opacity-60" style={{ color: GOLD }} />
                          <Input
                            type="time"
                            className="h-12 rounded-xl border-[rgba(212,175,55,0.22)] bg-black/25 pl-11 text-slate-100 focus-visible:ring-[rgba(212,175,55,0.55)] [color-scheme:dark]"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              {status.state === "error" ? (
                <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-200">
                  {status.message}
                </div>
              ) : null}

              <Button
                type="submit"
                className="h-12 w-full rounded-xl font-display text-xs font-bold uppercase tracking-[0.22em] text-black"
                style={{ backgroundColor: GOLD }}
                disabled={!canSubmit || status.state === "submitting"}
              >
                {status.state === "submitting" ? "Gönderiliyor..." : "Analizimi Oluştur"}
              </Button>
            </form>
          )}

          {!showNoAnalysisSuccess ? (
            <div className="mt-8 flex items-start gap-3 rounded-2xl border border-[rgba(212,175,55,0.12)] bg-black/20 p-4">
              <span className="mt-0.5 grid h-9 w-9 place-items-center rounded-xl border" style={{ borderColor: "rgba(212,175,55,0.18)" }}>
                <Info className="h-4 w-4" style={{ color: GOLD }} />
              </span>
              <p className="text-sm leading-relaxed text-slate-400">
                Bilgileriniz şifrelenmiş olarak iletilir. Kişisel numeroloji raporunuz uzmanlarımız tarafından hazırlanarak kayıtlı e-posta adresinize gönderilecektir.
              </p>
            </div>
          ) : null}
        </section>

        <div className="mt-12 flex items-center justify-center gap-4 opacity-40">
          <Sparkles className="h-4 w-4" style={{ color: GOLD }} />
          <Sparkles className="h-4 w-4" style={{ color: GOLD }} />
          <Sparkles className="h-4 w-4" style={{ color: GOLD }} />
        </div>
      </main>

      <footer className="mt-20 border-t py-12" style={{ borderColor: "rgba(212,175,55,0.10)" }}>
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-8 px-4">
          <div className="h-24 w-px bg-gradient-to-b from-[rgba(212,175,55,0.95)] to-transparent" />
          <div className="text-center">
            <div className="font-display text-3xl tracking-widest" style={{ color: GOLD }}>
              Numeroloji.app
            </div>
            <p className="mt-3 text-xs uppercase tracking-[0.22em] opacity-40">
              Evrensel Sayılar Rehberi © {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
